import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Progress, message } from 'antd';
import dayjs from 'dayjs';
import practiceClient from '../../client/PracticeClient';
import useTranslation from '../../hooks/useTranslation';
import './DeadlinePanel.css';

/**
 * План к дате: сколько нужно сделать сегодня, чтобы успеть.
 *
 * Панель отвечает на один вопрос — «успею ли», — и потому показывает не дату, а число:
 * дату человек и так помнит, а вот во что она обходится ежедневно, до этого экрана
 * узнать было негде.
 *
 * План считает сервер на каждый запрос. Держать его на клиенте нельзя: он зависит от
 * того, сколько уже сделано сегодня, и вчерашнее число сегодня уже неверно.
 */
const DeadlinePanel = ({ deckId, practiceType, onStartPlanned, isStarting }) => {
    const { t } = useTranslation();
    const [plan, setPlan] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(async (signal) => {
        try {
            const loaded = await practiceClient.getDeckDeadline(deckId, practiceType);
            if (!signal.cancelled) {
                setPlan(loaded);
            }
        } catch (error) {
            console.error('Не удалось загрузить план к дате:', error);
        }
    }, [deckId, practiceType]);

    useEffect(() => {
        if (!deckId) {
            return undefined;
        }
        const signal = { cancelled: false };
        load(signal);
        return () => {
            signal.cancelled = true;
        };
    }, [deckId, load]);

    const save = async (date) => {
        setIsSaving(true);
        try {
            setPlan(date
                ? await practiceClient.setDeckDeadline(deckId, practiceType, date.format('YYYY-MM-DD'))
                : await practiceClient.clearDeckDeadline(deckId, practiceType));
        } catch (error) {
            console.error('Не удалось сохранить дату:', error);
            message.error(t('practice.deadline.saveFailed'));
        } finally {
            setIsSaving(false);
        }
    };

    if (!deckId) {
        return null;
    }

    const examOn = plan?.examOn ? dayjs(plan.examOn) : null;
    const doneShare = plan?.dailyBudget > 0
        ? Math.min(100, Math.round((plan.doneToday / plan.dailyBudget) * 100))
        : 0;

    return (
        <section className="deadline">
            <div className="deadline__head">
                <div className="deadline__title">{t('practice.deadline.title')}</div>
                <DatePicker
                    className="deadline__picker"
                    value={examOn}
                    disabled={isSaving}
                    // Прошедшую дату выбрать нельзя: план в прошлое не строится, и
                    // сервер такую отклоняет — незачем давать нажать и получить ошибку.
                    disabledDate={(date) => date?.isBefore(dayjs().startOf('day'))}
                    onChange={save}
                    placeholder={t('practice.deadline.pick')}
                />
            </div>

            {!examOn && (
                <p className="deadline__hint">{t('practice.deadline.hint')}</p>
            )}

            {examOn && plan && !plan.available && (
                // Дата стоит, а плана нет: общее состояние ещё не читается, и «работа до
                // даты» была бы разной у каждого формата одной колоды.
                <p className="deadline__hint">{t('practice.deadline.unavailable')}</p>
            )}

            {/* Ноль в дневной доле значит одно из двух, и это разные новости. Дата
                прошла — плана больше нет, дней не осталось. Дата впереди, а работы нет —
                колода к ней готова. Раньше оба случая говорили «дата прошла», и человек,
                выучивший колоду заранее, получал сообщение о просроченном экзамене. */}
            {examOn && plan?.available && plan.dailyBudget === 0 && plan.daysLeft === 0 && (
                <p className="deadline__hint">{t('practice.deadline.passed')}</p>
            )}

            {examOn && plan?.available && plan.dailyBudget === 0 && plan.daysLeft > 0 && (
                <p className="deadline__hint">{t('practice.deadline.ready')}</p>
            )}

            {examOn && plan?.available && plan.dailyBudget > 0 && (
                <>
                    <div className="deadline__numbers">
                        <span className="deadline__budget">
                            {t('practice.deadline.perDay', { count: plan.dailyBudget })}
                        </span>
                        <span className="deadline__days">
                            {t('practice.deadline.daysLeft', { count: plan.daysLeft })}
                        </span>
                    </div>
                    <Progress
                        percent={doneShare}
                        size="small"
                        status={plan.remainingToday === 0 ? 'success' : 'active'}
                        format={() => `${plan.doneToday} / ${plan.dailyBudget}`}
                    />
                    <Button
                        className="deadline__start"
                        block
                        loading={isStarting}
                        disabled={plan.remainingToday === 0}
                        onClick={() => onStartPlanned?.()}
                    >
                        {plan.remainingToday === 0
                            ? t('practice.deadline.doneForToday')
                            : t('practice.deadline.start', { count: plan.remainingToday })}
                    </Button>
                </>
            )}
        </section>
    );
};

export default DeadlinePanel;
